/**
 * Atom: Textarea
 * Multiline text input with label, error support, and focus ring.
 * Supports optional inline translation via TranslateButton when user has a non-English preference.
 */
import React from 'react'
import { tokens } from '@/design-system/tokens'
import { TranslateButton } from './TranslateButton'

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  helperText?: string
  size?: 'sm' | 'md'
  /** When provided, shows a TranslateButton and calls this with the translated text */
  onTranslated?: (text: string) => void
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(({
  label,
  error,
  helperText,
  id,
  style,
  rows = 4,
  size = 'md',
  onTranslated,
  value,
  ...rest
}, ref) => {
  const inputId = id || `textarea-${label?.toLowerCase().replace(/\s+/g, '-')}`
  const isCompact = size === 'sm'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isCompact ? 3 : tokens.spacing[1] }}>
      {label && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <label htmlFor={inputId} style={{
            fontSize: isCompact ? '10px' : tokens.typography.fontSize.sm,
            fontWeight: '700',
            color: 'var(--text-muted)',
            fontFamily: tokens.typography.fontFamily.sans,
            textTransform: 'uppercase',
            letterSpacing: isCompact ? '0.07em' : '0.05em',
          }}>
            {label}
          </label>
          {onTranslated && (
            <TranslateButton
              text={String(value ?? '')}
              onTranslated={onTranslated}
              style={{ marginBottom: 0 }}
            />
          )}
        </div>
      )}
      {!label && onTranslated && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <TranslateButton
            text={String(value ?? '')}
            onTranslated={onTranslated}
          />
        </div>
      )}
      <style>{`
        textarea.ml-textarea-D1b:focus,
        textarea.ml-textarea-D1b:focus-visible {
          border-color: var(--brand-gold) !important;
          box-shadow: 0 0 0 3px var(--brand-gold-quiet);
        }
        textarea.ml-textarea-D1b.ml-err:focus,
        textarea.ml-textarea-D1b.ml-err:focus-visible {
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.18);
        }
      `}</style>
      <textarea
        id={inputId}
        rows={rows}
        value={value}
        className={['ml-textarea-D1b', error ? 'ml-err' : ''].filter(Boolean).join(' ')}
        style={{
          width: '100%',
          padding: isCompact ? '10px 12px' : `${tokens.spacing[2]} ${tokens.spacing[3]}`,
          borderRadius: tokens.radius.md,
          border: `1px solid ${error ? tokens.color.feedback.error : tokens.color.border.default}`,
          background: tokens.color.background.elevated,
          color: tokens.color.text.primary,
          fontFamily: tokens.typography.fontFamily.sans,
          fontSize: isCompact ? '13px' : 13,
          outline: 'none',
          resize: 'vertical',
          minHeight: isCompact ? 80 : undefined,
          boxSizing: 'border-box',
          transition: 'border-color 120ms ease, box-shadow 120ms ease',
          ...style,
        }}
        ref={ref}
        {...rest}
      />
      {error && (
        <span style={{ fontSize: 11, color: tokens.color.feedback.error, lineHeight: 1.4 }}>
          {error}
        </span>
      )}
      {helperText && !error && (
        <span style={{ fontSize: 11, color: tokens.color.text.muted, lineHeight: 1.4 }}>
          {helperText}
        </span>
      )}
    </div>
  )
})

Textarea.displayName = 'Textarea'
